import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { Form, FormFeedback, FormGroup, Input, Label } from 'reactstrap';
import { ColorResult, SketchPicker } from 'react-color';
import { twMerge } from 'tailwind-merge';

import { TypeMaster } from 'models/type/TypeMaster';
import { Table } from 'react-bootstrap';
import { toPascalCase } from 'helpers/utils';

type TypeMasterDisplayKey = Extract<keyof TypeMaster, `display_${string}`>;

const displayColumnKeys: TypeMasterDisplayKey[] = [
    'display_docno',
    'display_duedate',
    'display_paymentdate',
    'display_fiscalyear',
    'display_docdate',
    'display_bp',
    'display_remark',
    'display_approve_type',
    'display_amount',
    'display_currency',
    'display_created_by',
    'display_created_on',
    'display_updated_by',
    'display_updated_on',
    'display_duration',
    'display_channel_code',
    'display_channel_name'
];

export type WorkflowTypeMasterFormRefHandle = {
    submit: () => TypeMaster | undefined;
};

export type WorkflowTypeMasterFormProps = {
    disabledKeyField?: boolean;
    defaultValues?: TypeMaster;
    className?: string;
};

export const WorkflowTypeMasterForm = forwardRef<
    WorkflowTypeMasterFormRefHandle,
    WorkflowTypeMasterFormProps
>((props, ref) => {
    const { disabledKeyField, defaultValues, className } = props;

    const [formValues, setFormValues] = useState<TypeMaster>({
        sid: defaultValues?.sid ?? '',
        workflowtypeid: defaultValues?.workflowtypeid ?? '',
        workflowtypename: defaultValues?.workflowtypename ?? '',
        background_color: defaultValues?.background_color ?? '',
        display_docno: defaultValues?.display_docno ?? 'Y',
        display_duedate: defaultValues?.display_duedate ?? 'Y',
        display_paymentdate: defaultValues?.display_paymentdate ?? 'Y',
        display_fiscalyear: defaultValues?.display_fiscalyear ?? 'Y',
        display_docdate: defaultValues?.display_docdate ?? 'Y',
        display_bp: defaultValues?.display_bp ?? 'Y',
        display_remark: defaultValues?.display_remark ?? 'Y',
        display_approve_type: defaultValues?.display_approve_type ?? 'Y',
        display_amount: defaultValues?.display_amount ?? 'Y',
        display_currency: defaultValues?.display_currency ?? 'Y',
        display_created_by: defaultValues?.display_created_by ?? 'Y',
        display_created_on: defaultValues?.display_created_on ?? 'Y',
        display_updated_by: defaultValues?.display_updated_by ?? 'Y',
        display_updated_on: defaultValues?.display_updated_on ?? 'Y',
        display_duration: defaultValues?.display_duration ?? 'Y',
        display_channel_code: defaultValues?.display_channel_code ?? 'Y',
        display_channel_name: defaultValues?.display_channel_name ?? 'Y',
        created_by: defaultValues?.created_by ?? '',
        created_on: defaultValues?.created_on ?? '',
        updated_by: defaultValues?.updated_by ?? '',
        updated_on: defaultValues?.updated_on ?? ''
    });
    const [errors, setErrors] = useState<
        Partial<Record<keyof TypeMaster, string>>
    >({} as TypeMaster);

    const handleFormValueChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const key = event.currentTarget.name;
        const value = event.currentTarget.value;

        setFormValues({ ...formValues, [key]: value });
    };

    const handleFormDisplayColumnsValueChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const key = event.currentTarget.name;
        const checked = event.currentTarget.checked;

        setFormValues({ ...formValues, [key]: checked ? 'Y' : 'N' });
    };

    const handleColorPickerChange = (color: ColorResult) => {
        setFormValues({ ...formValues, background_color: color.hex });
    };

    const submitForm = () => {
        const errors: [keyof TypeMaster, string][] = [];
        if (!formValues.workflowtypeid) {
            errors.push(['workflowtypeid', 'กรุณากรอก ID']);
        }
        if (!formValues.workflowtypename) {
            errors.push(['workflowtypename', 'กรุณากรอกชื่อ']);
        }

        if (errors.length > 0) {
            setErrors(prevState => ({
                ...prevState,
                // @ts-ignore
                ...Object.fromEntries(errors)
            }));

            return undefined;
        }

        return { ...formValues };
    };

    useImperativeHandle(
        ref,
        () => ({
            submit: submitForm
        }),
        [submitForm]
    );

    return (
        <Form className={twMerge('flex flex-col', className)}>
            <FormGroup>
                <Label for="workflowtypeid">ID</Label>
                <Input
                    disabled={disabledKeyField}
                    invalid={!!errors.workflowtypeid}
                    id="workflowtypeid"
                    type="text"
                    name="workflowtypeid"
                    value={formValues.workflowtypeid}
                    onChange={
                        !disabledKeyField ? handleFormValueChange : undefined
                    }
                />
                <FormFeedback>{errors.workflowtypeid}</FormFeedback>
            </FormGroup>

            <FormGroup>
                <Label for="workflowtypename">Name</Label>
                <Input
                    invalid={!!errors.workflowtypename}
                    id="workflowtypename"
                    type="text"
                    name="workflowtypename"
                    value={formValues.workflowtypename}
                    onChange={handleFormValueChange}
                />
                <FormFeedback>{errors.workflowtypename}</FormFeedback>
            </FormGroup>

            <FormGroup>
                <Label for="background_color">Badge Color</Label>
                <Input
                    id="background_color"
                    type="text"
                    name="background_color"
                    value={formValues.background_color}
                    onChange={handleFormValueChange}
                />
            </FormGroup>

            <Label>Display Columns</Label>
            <div className="overflow-auto">
                <Table
                    size="sm"
                    className="table-bordered align-middle"
                >
                    <thead>
                        <tr className="table-active thead-light w-full text-center">
                            {displayColumnKeys.map(key => {
                                // @ts-ignore
                                const name = toPascalCase(key);

                                return <th key={`th-${key}`}>{name}</th>;
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            {displayColumnKeys.map(key => (
                                <td
                                    key={`td-${key}`}
                                    className="text-center align-middle"
                                >
                                    <input
                                        type="checkbox"
                                        name={key}
                                        defaultChecked={
                                            formValues?.[key] === 'Y'
                                        }
                                        onChange={
                                            handleFormDisplayColumnsValueChange
                                        }
                                    />
                                </td>
                            ))}
                        </tr>
                    </tbody>
                </Table>
            </div>

            <div className="mx-auto flex flex-col items-center gap-4">
                <div
                    style={{
                        borderBottom: `32px solid ${formValues.background_color}`
                    }}
                    className="trapezoid w-fit"
                >
                    <span>
                        <i
                            className="fa fa-search"
                            aria-hidden="true"
                        />
                        {`${formValues.workflowtypename || 'ตัวอย่าง'
                            } (เอกสาร 0 รายการ)`}
                    </span>
                </div>
                <SketchPicker
                    width="250px"
                    color={formValues.background_color}
                    onChange={handleColorPickerChange}
                />
            </div>
        </Form>
    );
});
